
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";
import { supabase } from "@/integrations/supabase/client";

interface EmailPreviewProps {
  templateType: string;
  templateData: any;
  onBack: () => void;
}

export function EmailPreview({ templateType, templateData, onBack }: EmailPreviewProps) {
  const { sendEmail, isSending } = useTransactionalEmail();
  const [isSendingCalculatorResults, setIsSendingCalculatorResults] = React.useState(false);
  
  const handleSendEmail = async () => {
    try {
      if (templateType === "calculator_results") {
        setIsSendingCalculatorResults(true);
        
        const { error } = await supabase.functions.invoke("send-calculator-results", {
          body: {
            email: templateData.email,
            firstName: templateData.firstName,
            county: templateData.county,
            preference: templateData.preference,
            results: templateData.results,
            formData: templateData.formData
          }
        });
        
        if (error) throw new Error(error.message);
        
        toast.success("Calculator results email sent successfully!");
      } else {
        await sendEmail({
          type: templateData.type || templateType,
          data: templateData
        });
        toast.success("Email sent successfully!");
      }
    } catch (error: any) {
      console.error("Error sending email:", error);
      toast.error(`Failed to send email: ${error.message}`);
    } finally {
      setIsSendingCalculatorResults(false);
    }
  };

  const getTemplateDescription = () => {
    switch (templateType) {
      case "welcome":
        return "Sent when a user creates an account";
      case "waitlist_confirmation":
        return "Sent when a user joins the waitlist";
      case "nursing_home_leads": 
        return "Sent when nursing homes are added to user's dashboard";
      case "claim_request":
        return "Sent when someone requests to claim a nursing home profile";
      case "nursing_home_inquiry":
        return "Sent when someone inquires about a nursing home";
      case "guide_download":
        return "Sent when someone downloads a guide";
      case "calculator_results":
        return "Sent when someone requests their Fair Deal calculator results";
      default:
        return "Transactional email template";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Preview</CardTitle>
        <CardDescription>{getTemplateDescription()}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="rounded-md border p-4">
            <h3 className="font-medium mb-2">Template: {templateType}</h3>
            <div className="text-sm text-muted-foreground">
              <p><strong>To:</strong> {templateData.email}</p>
              <p><strong>From:</strong> Grandpal &lt;hello@updates.grandpal.co&gt;</p>
              <p className="mt-2"><strong>Template data:</strong></p>
              <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto max-h-48 whitespace-pre-wrap">
                {JSON.stringify(templateData, null, 2)}
              </pre>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
            Email will be sent using the Resend API. Recipients will receive the actual email.
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button 
          onClick={handleSendEmail} 
          disabled={isSending || isSendingCalculatorResults}
        >
          {isSending || isSendingCalculatorResults ? "Sending..." : "Send Test Email"}
        </Button>
      </CardFooter>
    </Card>
  );
}
